import React, { FC, useCallback, useReducer, useState, useEffect } from 'react';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks';
import { action } from 'typesafe-actions';
import { isEmpty } from '@dxb-web-ui-framework/common/utils/Helper';
import { subYears } from 'date-fns';
import { useParams } from 'react-router-dom';
import { Box } from '@dxb-web-ui-framework/design-system';
import { payrollService } from '../../../../../channels/secure-web/services';
import { ECOMMBXLoader } from '../../../../content-composer/src/components/Generic/ECOMMBXLoader/ECOMMBXLoader';
import { getLimitDateFormat } from '../../../../content-composer/src/utils';
import { useSelectedCustomerId } from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { Payroll } from '../../domain/Payroll/entities/types';
import { PayrollContext, PayrollDispatchContext } from './PayrollContext';
import { defaultPayrollState } from './defaultState';
import { PayrollReducer } from './PayrollReducer';
import { PayrollContextActionTypes } from './types';

export interface PayrollProviderProps {
  shouldShowChildrenWhenLoading?: boolean;
}

const PayrollProvider: FC<PayrollProviderProps> = ({ children, shouldShowChildrenWhenLoading = false }) => {
  // Get initial state values
  const [state, dispatch] = useReducer(PayrollReducer, defaultPayrollState);
  const [timestamp] = useState(new Date().toISOString());
  const customerId = useSelectedCustomerId();
  const { payrollId } = useParams();

  // Define api call conditions
  const shouldFetchPayrolls = Boolean(customerId);

  // Get payroll files data
  const {
    data: payrollListData,
    isLoading: isLoadingPayrollListData,
    error: payrollListDataError,
  } = useInvokeSWRService<Payroll[]>(
    payrollService.GetPayrollList,
    {
      customerId,
      dateFrom: getLimitDateFormat(subYears(new Date(), 1), true),
      dateTo: getLimitDateFormat(new Date()),
      meta: {
        timestamp,
      },
    },
    shouldFetchPayrolls,
  );

  // Payroll file details api call definition
  const shouldFetchPayrollDetails = !isEmpty(payrollListData) && !isEmpty(state.selectedPayroll);
  const {
    data: payrollDetailsData,
    error: payrollDetailsError,
    isLoading: isLoadingPayrollDetails,
  } = useInvokeSWRService(
    payrollService.GetPayrollDetails,
    {
      customerId,
      fileId: state.selectedPayroll?.id,
      meta: {
        timestamp,
      },
    },
    shouldFetchPayrollDetails,
  );

  const onSelectPayroll = useCallback(
    (payrollFileId?: number) => {
      if (payrollFileId && payrollListData) {
        const payrollFile = payrollListData.find(payrollFile => payrollFile.id === payrollFileId);
        dispatch(action(PayrollContextActionTypes.SetSelectedPayroll, payrollFile || null));
      } else {
        dispatch(action(PayrollContextActionTypes.SetSelectedPayroll, null));
      }
    },
    [payrollListData],
  );

  useEffect(() => {
    if (payrollId && payrollListData) {
      onSelectPayroll(Number(payrollId));
    }
  }, [payrollId, payrollListData, onSelectPayroll]);

  const onResetSelectedPayroll = useCallback(() => {
    dispatch(action(PayrollContextActionTypes.SetSelectedPayroll, null));
  }, []);

  return (
    <PayrollContext.Provider
      value={{
        ...state,
        payrollListData,
        payrollDetailsData,
        loadingState: {
          isLoadingPayrollListData,
          isLoadingPayrollDetails,
        },
        errorState: {
          payrollListDataError,
          payrollDetailsError,
        },
        onResetSelectedPayroll,
        onSelectPayroll,
      }}>
      <PayrollDispatchContext.Provider value={dispatch}>
        {!shouldShowChildrenWhenLoading && isLoadingPayrollListData && (
          <Box style={{ height: '100vh', position: 'absolute', top: '50%', right: '50%' }}>
            <ECOMMBXLoader size='full' verticalSpacing='large' />
          </Box>
        )}
        {(shouldShowChildrenWhenLoading || (!shouldShowChildrenWhenLoading && payrollListData)) && children}
      </PayrollDispatchContext.Provider>
    </PayrollContext.Provider>
  );
};

export { PayrollProvider };
